import { useState } from 'react';
import { MapPin, Edit, Check, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

export const LocationCard = ({ tag, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [address, setAddress] = useState(tag.location);
  const [latitude, setLatitude] = useState(tag.coordinates?.lat || '');
  const [longitude, setLongitude] = useState(tag.coordinates?.lng || '');

  const hasLocation = tag.location && tag.location !== '-';

  const handleSave = () => {
    if (!address || !latitude || !longitude) {
      toast.error('Todos os campos de localização são obrigatórios!');
      return;
    }

    onUpdate?.({
      location: address,
      coordinates: {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude)
      }
    });
    setIsEditing(false);
    toast.success('Localização atualizada com sucesso!');
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toFixed(6));
          setLongitude(position.coords.longitude.toFixed(6));
          toast.success('Localização GPS obtida!');
        },
        (error) => {
          toast.error('Erro ao obter localização GPS');
        }
      );
    } else {
      toast.error('Geolocalização não suportada pelo navegador');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`${hasLocation ? 'bg-emerald-50' : 'bg-amber-50'} p-3 rounded-lg`}>
            <MapPin className={hasLocation ? 'text-emerald-600' : 'text-amber-600'} size={24} />
          </div>
          <div>
            <h3 className="text-lg font-secondary font-bold text-slate-900">Localização Física</h3>
            <p className="text-sm text-slate-600">
              {hasLocation ? 'Definida' : 'Não definida - Obrigatório'}
            </p>
          </div>
        </div>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Edit size={16} className="mr-2" />
              {hasLocation ? 'Editar' : 'Definir'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Definir Localização Física</DialogTitle>
              <DialogDescription>
                Defina a localização exata onde a tag está instalada
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="address">Endereço / Descrição do Local *</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Ex: Shopping Colombo - Piso -2, Setor B, Vaga 45"
                />
                <p className="text-xs text-slate-500">
                  Seja específico: inclua edifício, andar, setor, número da vaga
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude (GPS) *</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="0.000001"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder="38.756912"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude (GPS) *</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="0.000001"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder="-9.186943"
                  />
                </div>
              </div>

              <Button 
                variant="outline" 
                onClick={handleGetCurrentLocation}
                className="w-full"
              >
                <MapPin size={16} className="mr-2" />
                Usar Localização Atual (GPS)
              </Button>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-2">💡 Como obter coordenadas GPS:</p>
                <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Abra Google Maps no telemóvel ou computador</li>
                  <li>Encontre o local exato do posto</li>
                  <li>Clique longo no mapa no local exato</li>
                  <li>As coordenadas aparecem na parte inferior</li>
                  <li>Copie e cole aqui</li>
                </ol>
              </div>

              {latitude && longitude && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-sm font-medium text-slate-900 mb-2">📍 Preview da Localização</p>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <MapPin size={14} />
                    <span className="font-mono">{latitude}, {longitude}</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                  >
                    Abrir no Google Maps →
                  </a>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                <Check size={16} className="mr-2" />
                Guardar Localização
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {hasLocation ? (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-start gap-3 mb-3">
              <MapPin className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900 mb-1">{tag.location}</p>
                {tag.coordinates && (
                  <p className="text-xs text-slate-600 font-mono">
                    GPS: {tag.coordinates.lat}, {tag.coordinates.lng}
                  </p>
                )}
              </div>
            </div>
            
            {tag.coordinates && (
              <>
                <div className="w-full h-48 bg-slate-200 rounded-lg overflow-hidden relative mb-3">
                  {mapError ? (
                    <div className="flex items-center justify-center h-full bg-slate-200">
                      <div className="text-center">
                        <MapPin className="mx-auto mb-2 text-slate-500" size={48} />
                        <p className="text-sm text-slate-600">Mapa do local</p>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l-marker+2563eb(${tag.coordinates.lng},${tag.coordinates.lat})/${tag.coordinates.lng},${tag.coordinates.lat},15,0/600x300@2x?access_token=pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw`}
                      alt="Mapa"
                      className="w-full h-full object-cover"
                      onError={() => setMapError(true)}
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <a
                    href={`https://www.google.com/maps?q=${tag.coordinates.lat},${tag.coordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Abrir no Google Maps
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${tag.coordinates.lat},${tag.coordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center px-3 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors"
                  >
                    Obter Direções
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="p-6 bg-amber-50 rounded-lg border-2 border-amber-300 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-3">
            <MapPin className="text-amber-600" size={32} />
          </div>
          <h4 className="text-lg font-secondary font-bold text-amber-900 mb-2">
            Localização Não Definida
          </h4>
          <p className="text-sm text-amber-700 mb-4">
            É obrigatório definir a localização física exata desta tag para que os utilizadores possam reportar problemas corretamente.
          </p>
          <Button onClick={() => setIsEditing(true)} className="bg-amber-600 hover:bg-amber-700">
            <MapPin size={16} className="mr-2" />
            Definir Localização Agora
          </Button>
        </div>
      )}
    </Card>
  );
};
